import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../Shared/axiosInstance";
import { BASE_URL } from "../../Shared/URL.js";
import CustomDatePicker from "../CustomDate/CustomDatePicker.jsx";

export default function SaleForm() {
  const API_BASE = `${BASE_URL}/Sale`;
  const CUSTOMERS_API = `${BASE_URL}/Customer/all`;
  const PRODUCTS_API = `${BASE_URL}/Product`;
  const IN_STOCK_API = `${BASE_URL}/Product/in-stock`;

  const navigate = useNavigate();

  // States
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Load customers
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoadingCustomers(true);
        const { data } = await axiosInstance.get(CUSTOMERS_API, {
          params: { name: customerSearch || undefined, pageNumber: 1, pageSize: 15 },
        });
        setCustomers(data.data || []);
      } catch (err) {
        Swal.fire("Error", "Failed to load customers", "error");
      } finally {
        setLoadingCustomers(false);
      }
    };
    const delay = setTimeout(fetchCustomers, 300);
    return () => clearTimeout(delay);
  }, [customerSearch]);

  // Load products
  useEffect(() => {
    if (!showModal) return;
    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const { data } = await axiosInstance.get(IN_STOCK_API, {
          params: { name: productSearch || undefined, pageNumber: 1, pageSize: 15 },
        });
        setProducts(data.data || []);
      } catch (err) {
        Swal.fire("Error", "Failed to load products", "error");
      } finally {
        setLoadingProducts(false);
      }
    };
    const delay = setTimeout(fetchProducts, 300);
    return () => clearTimeout(delay);
  }, [productSearch, showModal]);

  // Formik + Yup Validation
  const formik = useFormik({
    initialValues: {
      customerId: "",
      saleDate: new Date().toISOString().slice(0, 10),
      items: [{ productId: "", productName: "", quantity: 0, sellPrice: 0 }],
      payments: [{ amount: 0, date: new Date().toISOString().slice(0, 10), method: "Cash" }],
      discountAmount: 0,
    },
    validationSchema: Yup.object({
      customerId: Yup.string().required("Customer is required"),
      saleDate: Yup.string().required("Sale Date is required"),
      items: Yup.array().of(
        Yup.object({
          productId: Yup.string().required("Select a product"),
          quantity: Yup.number().min(1, "Quantity must be at least 1").required("Required"),
          sellPrice: Yup.number().min(0.01, "Price must be greater than 0").required("Required"),
        })
      ),
      payments: Yup.array().of(
        Yup.object({
          amount: Yup.number().min(0.01, "Amount must be greater than 0").required("Required"),
          date: Yup.string().required("Payment date is required"),
          method: Yup.string().required("Select payment method"),
        })
      ),
    }),
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        const payload = {
          customerId: values.customerId,
          saleDate: values.saleDate,
          items: values.items.map((i) => ({ productId: i.productId, quantity: i.quantity, unitPrice: i.sellPrice })),
          payments: values.payments.map((p) => ({ amount: p.amount, date: p.date, method: p.method })),
          discountAmount: parseFloat(values.discountAmount) || 0,
        };
        const { data } = await axiosInstance.post(API_BASE, payload);

        await Swal.fire({
          title: "نجاح!",
          text: data.message || "تم انشاء فاتورة بيع بنجاح",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });

        navigate("/sales");
        formik.resetForm();
      } catch (err) {
        const message = err.response?.data?.error || "Something went wrong";
        Swal.fire("Error", message, "error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const total = formik.values.items.reduce((acc, item) => acc + (item.quantity * item.sellPrice), 0);
  const totalAfterDiscount = total - (parseFloat(formik.values.discountAmount) || 0);

  // Add / Remove Items & Payments
  const addNewItem = () => formik.setFieldValue("items", [...formik.values.items, { productId: "", productName: "", quantity: 0, sellPrice: 0 }]);
  const removeItem = (index) => {
    const newItems = formik.values.items.filter((_, i) => i !== index);
    formik.setFieldValue("items", newItems.length ? newItems : [{ productId: "", productName: "", quantity: 0, sellPrice: 0 }]);
  };
  const addNewPayment = () => formik.setFieldValue("payments", [...formik.values.payments, { amount: 0, date: new Date().toISOString().slice(0, 10), method: "Cash" }]);
  const removePayment = (index) => {
    const newPayments = formik.values.payments.filter((_, i) => i !== index);
    formik.setFieldValue("payments", newPayments.length ? newPayments : [{ amount: 0, date: new Date().toISOString().slice(0, 10), method: "Cash" }]);
  };

  // Modal
  const openProductModal = (index) => {
    setCurrentItemIndex(index);
    setShowModal(true);
    setProductSearch("");
  };
  const selectProduct = async (product) => {
    if (currentItemIndex === null) return;
    formik.setFieldValue(`items[${currentItemIndex}].productId`, product.id);
    formik.setFieldValue(`items[${currentItemIndex}].productName`, `${product.name} - ${product.description}`);

    try {
      const { data } = await axiosInstance.get(`${PRODUCTS_API}/${product.id}`);
      const pd = data.data || {};
      formik.setFieldValue(`items[${currentItemIndex}].sellPrice`, pd.sellPrice || 0);
      formik.setFieldValue(`items[${currentItemIndex}].quantity`, pd.quantityInStock || 0);
    } catch (err) {
      console.error("Error fetching product details:", err);
    }

    setShowModal(false);
    setCurrentItemIndex(null);
  };

  const totalPayments = formik.values.payments.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0);
  const remainingAfterDiscount = totalAfterDiscount - totalPayments;

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
      <div className="container py-5" style={{ maxWidth: "1000px" }}>
        
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4 mt-5 pt-5">
          <div>
            <h2 style={{ color: "#2563eb", fontWeight: "700", fontSize: "28px", marginBottom: "4px" }}>
              <i className="bi bi-receipt me-2"></i>إنشاء عملية بيع
            </h2>
            <small style={{ color: "#64748b", fontSize: "14px" }}>
              إضافة طلب بيع جديد من المخزون
            </small>
          </div>
          <button
            type="button"
            onClick={() => navigate("/sales")}
            style={{
              background: "#64748b",
              color: "#fff",
              padding: "10px 20px",
              borderRadius: "6px",
              border: "none",
              fontWeight: "600",
              fontSize: "15px",
              cursor: "pointer",
            }}
          >
            <i className="bi bi-arrow-left me-1"></i> رجوع
          </button>
        </div>

        <form onSubmit={formik.handleSubmit}>
          
          {/* Customer & Date Section */}
          <div style={cardStyle}>
            <h5 style={sectionTitleStyle}>
              <i className="bi bi-person-circle me-2"></i>بيانات العميل
            </h5>
            
            <div className="row g-3">
              <div className="col-md-8">
                <label style={labelStyle}>ابحث عن العميل</label>
                <input
                  type="text"
                  placeholder="🔍 اكتب اسم العميل..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  style={inputStyle}
                />
                <select
                  name="customerId"
                  value={formik.values.customerId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{ ...inputStyle, marginTop: "8px" }}
                >
                  <option value="">-- اختر العميل --</option>
                  {loadingCustomers ? (
                    <option disabled>جاري التحميل...</option>
                  ) : customers.length > 0 ? (
                    customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} - {c.details}
                      </option>
                    ))
                  ) : (
                    <option disabled>لا يوجد عملاء</option>
                  )}
                </select>
                {formik.touched.customerId && formik.errors.customerId && (
                  <div style={errorStyle}>{formik.errors.customerId}</div>
                )}
              </div>

              <div className="col-md-4">
                <CustomDatePicker
                  label="تاريخ البيع"
                  value={formik.values.saleDate}
                  onChange={(date) => formik.setFieldValue('saleDate', date)}
                  placeholder="اختر تاريخ البيع"
                />
                {formik.touched.saleDate && formik.errors.saleDate && (
                  <div style={errorStyle}>{formik.errors.saleDate}</div>
                )}
              </div>
            </div>
          </div>

          {/* Products Section */}
          <div style={cardStyle}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 style={sectionTitleStyle}>
                <i className="bi bi-box-seam me-2"></i>المنتجات ({formik.values.items.length})
              </h5>
              <button
                type="button"
                onClick={addNewItem}
                style={{
                  background: "#2563eb",
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "none",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                <i className="bi bi-plus-circle me-1"></i> إضافة منتج
              </button>
            </div>

            {/* Compact Table Header */}
            <div className="row g-2 mb-2" style={{ 
              padding: "8px 12px", 
              background: "#f1f5f9", 
              borderRadius: "6px",
              fontWeight: "600",
              fontSize: "13px",
              color: "#475569"
            }}>
              <div className="col-1 text-center">#</div>
              <div className="col-4">المنتج</div>
              <div className="col-2 text-center">الكمية</div>
              <div className="col-2 text-center">سعر البيع</div>
              <div className="col-2 text-center">المجموع</div>
              <div className="col-1"></div>
            </div>

            {formik.values.items.map((item, index) => (
              <div key={index} style={{
                ...compactItemStyle,
                borderBottom: index < formik.values.items.length - 1 ? "1px solid #e2e8f0" : "none"
              }}>
                <div className="row g-2 align-items-center">
                  {/* Row Number */}
                  <div className="col-1 text-center">
                    <span style={{ color: "#2563eb", fontWeight: "600", fontSize: "14px" }}>
                      {index + 1}
                    </span>
                  </div>

                  {/* Product Selection */}
                  <div className="col-4">
                    <div className="d-flex gap-1">
                      <input
                        type="text"
                        value={item.productName || "لم يتم اختيار منتج"}
                        readOnly
                        style={{ 
                          ...compactInputStyle, 
                          flex: 1, 
                          background: "#f8fafc", 
                          cursor: "default",
                          fontSize: "13px"
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => openProductModal(index)}
                        style={{
                          background: "#2563eb",
                          color: "#fff",
                          padding: "6px 12px",
                          borderRadius: "4px",
                          border: "none",
                          fontWeight: "600",
                          fontSize: "12px",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <i className="bi bi-search"></i>
                      </button>
                    </div>
                    {formik.touched.items?.[index]?.productId &&
                      formik.errors.items?.[index]?.productId && (
                        <div style={{...errorStyle, fontSize: "11px"}}>{formik.errors.items[index].productId}</div>
                      )}
                  </div>

                  {/* Quantity */}
                  <div className="col-2">
                    <input
                      name={`items[${index}].quantity`}
                      type="number"
                      min="0"
                      value={item.quantity}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onWheel={(e) => e.target.blur()}
                      style={{...compactInputStyle, textAlign: "center"}}
                    />
                    {formik.touched.items?.[index]?.quantity &&
                      formik.errors.items?.[index]?.quantity && (
                        <div style={{...errorStyle, fontSize: "11px"}}>{formik.errors.items[index].quantity}</div>
                      )}
                  </div>

                  {/* Sell Price */}
                  <div className="col-2">
                    <input
                      name={`items[${index}].sellPrice`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.sellPrice}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onWheel={(e) => e.target.blur()}
                      style={{...compactInputStyle, textAlign: "center"}}
                    />
                    {formik.touched.items?.[index]?.sellPrice &&
                      formik.errors.items?.[index]?.sellPrice && (
                        <div style={{...errorStyle, fontSize: "11px"}}>{formik.errors.items[index].sellPrice}</div>
                      )}
                  </div>

                  {/* Subtotal */}
                  <div className="col-2 text-center">
                    <span style={{ 
                      color: "#1e40af", 
                      fontWeight: "600", 
                      fontSize: "13px",
                      background: "#dbeafe",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      display: "inline-block"
                    }}>
                      {((item.quantity || 0) * (item.sellPrice || 0)).toFixed(2)}
                    </span>
                  </div>

                  {/* Remove Button */}
                  <div className="col-1 text-center">
                    {formik.values.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        style={{
                          background: "#dc2626",
                          color: "#fff",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "none",
                          fontSize: "12px",
                          cursor: "pointer",
                        }}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Total Summary */}
          <div style={{
            background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
            padding: "20px",
            borderRadius: "8px",
            marginBottom: "20px",
            boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
          }}>
            <div className="d-flex justify-content-between align-items-center">
              <span style={{ color: "#fff", fontSize: "18px", fontWeight: "600" }}>
                <i className="bi bi-calculator me-2"></i>إجمالي البيع
              </span>
              <span style={{ color: "#fff", fontSize: "28px", fontWeight: "700" }}>
                {total.toFixed(2)} جنيه
              </span>
            </div>
          </div>

          {/* Discount Section */}
          <div style={cardStyle}>
            <h5 style={{...sectionTitleStyle, fontSize: "20px", marginBottom: "20px"}}>
              <i className="bi bi-tag me-2"></i>الخصم
            </h5>
            <div className="mb-3" style={{ maxWidth: "400px" }}>
              <label style={{...labelStyle, fontSize: "16px", marginBottom: "10px"}}>قيمة الخصم (جنيه)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                name="discountAmount"
                placeholder="0.00"
                value={formik.values.discountAmount || 0}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                onWheel={(e) => e.target.blur()}
                style={{...inputStyle, padding: "14px 16px", fontSize: "16px"}}
              />
            </div>

            {/* Summary after discount */}
            <div style={{
              background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
              padding: "24px",
              borderRadius: "10px",
              marginTop: "20px",
              borderLeft: "5px solid #2563eb",
              boxShadow: "0 2px 8px rgba(37, 99, 235, 0.15)"
            }}>
              <div className="d-flex justify-content-between align-items-center">
                <span style={{ color: "#1e40af", fontWeight: "700", fontSize: "20px" }}>
                  الإجمالي بعد الخصم:
                </span>
                <span style={{ color: "#1e40af", fontWeight: "800", fontSize: "26px" }}>
                  {totalAfterDiscount.toFixed(2)} جنيه
                </span>
              </div>
            </div>
          </div>

          {/* Payments Section */}
          <div style={cardStyle}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 style={sectionTitleStyle}>
                <i className="bi bi-cash-coin me-2"></i>المدفوعات ({formik.values.payments.length})
              </h5>
              <button
                type="button"
                onClick={addNewPayment}
                style={{
                  background: "#22c55e",
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "none",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                <i className="bi bi-plus-circle me-1"></i> إضافة دفعة
              </button>
            </div>

            {formik.values.payments.map((p, index) => (
              <div key={index} style={paymentCardStyle}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span style={{ color: "#22c55e", fontWeight: "600", fontSize: "15px" }}>
                    الدفعة #{index + 1}
                  </span>
                  {formik.values.payments.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePayment(index)}
                      style={{
                        background: "#dc2626",
                        color: "#fff",
                        padding: "4px 12px",
                        borderRadius: "4px",
                        border: "none",
                        fontSize: "13px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      <i className="bi bi-trash me-1"></i> إزالة
                    </button>
                  )}
                </div>

                <div className="row g-3">
                  <div className="col-md-4">
                    <label style={labelStyle}>المبلغ (جنيه)</label>
                    <input
                      name={`payments[${index}].amount`}
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={p.amount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onWheel={(e) => e.target.blur()}
                      style={inputStyle}
                    />
                    {formik.touched.payments?.[index]?.amount &&
                      formik.errors.payments?.[index]?.amount && (
                        <div style={errorStyle}>{formik.errors.payments[index].amount}</div>
                      )}
                  </div>

                  <div className="col-md-4">
                    <CustomDatePicker
                      label="تاريخ الدفع"
                      value={p.date}
                      onChange={(date) => formik.setFieldValue(`payments[${index}].date`, date)}
                      placeholder="اختر تاريخ الدفع"
                    />
                  </div>

                  <div className="col-md-4">
                    <label style={labelStyle}>طريقة الدفع</label>
                    <select
                      name={`payments[${index}].method`}
                      value={p.method}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={inputStyle}
                    >
                      <option value="Cash">💵 نقدي</option>
                      <option value="VodafoneCash">📱 فودافون كاش</option>
                      <option value="InstaBay">💳 إنستا باي</option>
                    </select>
                    {formik.touched.payments?.[index]?.method &&
                      formik.errors.payments?.[index]?.method && (
                        <div style={errorStyle}>{formik.errors.payments[index].method}</div>
                      )}
                  </div>
                </div>
              </div>
            ))}

            {/* Payment Summary */}
            <div style={{
              background: "#f0fdf4",
              padding: "16px",
              borderRadius: "6px",
              marginTop: "16px",
              border: "1px solid #86efac",
            }}>
              <div className="d-flex justify-content-between mb-2">
                <span style={{ color: "#166534", fontWeight: "600" }}>إجمالي المدفوعات:</span>
                <span style={{ color: "#166534", fontWeight: "700", fontSize: "18px" }}>
                  {totalPayments.toFixed(2)} جنيه
                </span>
              </div>
              <div className="d-flex justify-content-between">
                <span style={{ color: remainingAfterDiscount > 0 ? "#dc2626" : "#166534", fontWeight: "600" }}>
                  المتبقي بعد الخصم:
                </span>
                <span style={{ 
                  color: remainingAfterDiscount > 0 ? "#dc2626" : "#166534", 
                  fontWeight: "700", 
                  fontSize: "18px" 
                }}>
                  {remainingAfterDiscount.toFixed(2)} جنيه
                </span>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: "100%",
              background: submitting ? "#94a3b8" : "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
              color: "#fff",
              padding: "16px",
              borderRadius: "8px",
              border: "none",
              fontWeight: "700",
              fontSize: "18px",
              cursor: submitting ? "not-allowed" : "pointer",
              boxShadow: "0 4px 12px rgba(34, 197, 94, 0.3)",
              transition: "all 0.3s ease",
            }}
          >
            <i className={`bi ${submitting ? "bi-hourglass-split" : "bi-check-circle"} me-2`}></i>
            {submitting ? "جاري الحفظ..." : "حفظ البيع"}
          </button>
        </form>
      </div>

      {/* Product Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "700px",
              maxHeight: "80vh",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: "20px 24px",
              borderBottom: "1px solid #e2e8f0",
              background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
            }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 style={{ color: "#fff", fontWeight: "700", fontSize: "20px", margin: 0 }}>
                  <i className="bi bi-search me-2"></i>اختر المنتج
                </h5>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    cursor: "pointer",
                    fontSize: "20px",
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "20px 24px" }}>
              <input
                type="text"
                placeholder="🔍 ابحث عن المنتجات بالاسم..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                autoFocus
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #bfdbfe",
                  borderRadius: "8px",
                  fontSize: "15px",
                  marginBottom: "16px",
                  outline: "none",
                }}
              />

              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {loadingProducts ? (
                  <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                    <div className="spinner-border text-primary" role="status"></div>
                    <p style={{ marginTop: "12px" }}>جاري تحميل المنتجات...</p>
                  </div>
                ) : products.length > 0 ? (
                  <div>
                    {products.map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => selectProduct(product)}
                        style={{
                          width: "100%",
                          padding: "16px",
                          marginBottom: "8px",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          background: "#fff",
                          textAlign: "left",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f0f9ff";
                          e.currentTarget.style.borderColor = "#2563eb";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#fff";
                          e.currentTarget.style.borderColor = "#e2e8f0";
                        }}
                      >
                        <div style={{ fontWeight: "600", color: "#1e293b", marginBottom: "4px" }}>
                          {product.name}
                        </div>
                        <div style={{ fontSize: "14px", color: "#64748b" }}>
                          {product.description}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                    <i className="bi bi-inbox" style={{ fontSize: "48px", marginBottom: "12px" }}></i>
                    <p>لا توجد منتجات</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ✅ Styles
const cardStyle = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  padding: "24px",
  marginBottom: "20px",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
};

const sectionTitleStyle = {
  color: "#1e293b",
  fontWeight: "700",
  fontSize: "18px",
  marginBottom: "20px",
};

const labelStyle = {
  color: "#475569",
  fontSize: "14px",
  fontWeight: "600",
  marginBottom: "6px",
  display: "block",
};

const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  border: "1px solid #cbd5e1",
  borderRadius: "6px",
  fontSize: "15px",
  color: "#1e293b",
  outline: "none",
  transition: "all 0.2s",
};

const errorStyle = {
  color: "#dc2626",
  fontSize: "13px",
  marginTop: "4px",
  fontWeight: "500",
};

const itemCardStyle = {
  background: "#f8fafc",
  border: "2px solid #e0e7ff",
  borderRadius: "8px",
  padding: "16px",
  marginBottom: "12px",
};

const paymentCardStyle = {
  background: "#f0fdf4",
  border: "2px solid #bbf7d0",
  borderRadius: "8px",
  padding: "16px",
  marginBottom: "12px",
};

const compactItemStyle = {
  padding: "12px 8px",
  marginBottom: "6px",
  transition: "background 0.2s ease",
};

const compactInputStyle = {
  width: "100%",
  padding: "6px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: "4px",
  fontSize: "13px",
  color: "#1e293b",
  outline: "none",
  transition: "all 0.2s",
};


